/**
 * Policy & Procedure Page - Home Health Policy Manager
 * Perplexity-style Policy Management System with Admin Editing
 * 121 policies across 7 categories with state jurisdiction support
 */

import React, { useState, useMemo, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../api';
import {
  FileText, Search, ChevronRight, Users, Heart, Shield,
  AlertTriangle, Briefcase, FolderOpen, Settings, ArrowLeft, 
  Moon, Sun, BookOpen, Building2, ClipboardList, Database,
  Edit, Plus, Trash2, Save, X, Download
} from 'lucide-react';

// US States for jurisdiction selector
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

// Icon mapping
const ICONS = {
  Users, Heart, Shield, ClipboardList, Briefcase, Database, FolderOpen, FileText
};

// Default chapters (used when backend has no data)
const DEFAULT_CHAPTERS = [
  { chapterId: 1, title: "Patient Rights & Responsibilities", policyCount: 2, icon: "Users", color: "#3b82f6", description: "Policies establishing patient rights, responsibilities, and protections under Medicare CoP" },
  { chapterId: 2, title: "Provision of Care", policyCount: 44, icon: "Heart", color: "#ec4899", description: "Comprehensive policies covering patient assessment, care planning, skilled services, and medication management" },
  { chapterId: 3, title: "Infection Control", policyCount: 18, icon: "Shield", color: "#10b981", description: "Policies for infection prevention, control measures, standard precautions, and OSHA compliance" },
  { chapterId: 4, title: "Quality Improvement", policyCount: 5, icon: "ClipboardList", color: "#f59e0b", description: "Quality Assessment and Performance Improvement (QAPI) policies and procedures" },
  { chapterId: 5, title: "Leadership & Human Resources", policyCount: 15, icon: "Briefcase", color: "#8b5cf6", description: "Staff qualifications, training requirements, competency evaluations, and HR policies" },
  { chapterId: 6, title: "Records Management", policyCount: 12, icon: "Database", color: "#06b6d4", description: "Medical records, documentation standards, HIPAA compliance, and information management" },
  { chapterId: 7, title: "Administrative & Compliance", policyCount: 25, icon: "FolderOpen", color: "#ef4444", description: "Organization policies, governance, emergency preparedness, and compliance procedures" }
];

// Default policies structure
const DEFAULT_POLICIES = {
  1: [
    { id: "1.1", title: "Home Care Patient Rights and Responsibilities", hasStateInserts: true },
    { id: "1.2", title: "Patient Bill of Rights", hasStateInserts: false }
  ],
  2: [
    { id: "2.1", title: "Human Rights Assurance", hasStateInserts: false },
    { id: "2.2", title: "Patient Admission Criteria / Scope of Services", hasStateInserts: true },
    { id: "2.3", title: "Identifying and Reporting Possible Victims of Alleged/Suspected Abuse or Neglect", hasStateInserts: true },
    { id: "2.4", title: "RN Delegation", hasStateInserts: false },
    { id: "2.5", title: "Initial Assessments / Comprehensive Assessments", hasStateInserts: false },
    { id: "2.6", title: "Plan of Care", hasStateInserts: false },
    { id: "2.7", title: "Professional Standards of Practice", hasStateInserts: false },
    { id: "2.8", title: "Coordination of Patient Care", hasStateInserts: false }
  ],
  3: [
    { id: "3.1", title: "Infection Control Program", hasStateInserts: false },
    { id: "3.2", title: "Employee Training", hasStateInserts: false },
    { id: "3.3", title: "OSHA Compliance", hasStateInserts: false }
  ],
  4: [
    { id: "4.1", title: "Quality Monitoring Program", hasStateInserts: false },
    { id: "4.2", title: "OASIS Data Management", hasStateInserts: false }
  ],
  5: [
    { id: "5.1", title: "Staffing & Recruitment", hasStateInserts: false },
    { id: "5.2", title: "HR Policies & Procedures", hasStateInserts: false }
  ],
  6: [
    { id: "6.1", title: "Medical Records Management", hasStateInserts: true },
    { id: "6.2", title: "Documentation Standards", hasStateInserts: false }
  ],
  7: [
    { id: "7.1", title: "Informed Consent", hasStateInserts: false },
    { id: "7.2", title: "Advance Directives & DNR Orders", hasStateInserts: true }
  ]
};

// Generate default policy content
const generateDefaultContent = (policyId, title, state) => ({
  policyStatement: `It is the policy of Agency to maintain comprehensive standards and procedures for ${title.toLowerCase()} in accordance with Medicare Conditions of Participation, state regulations, and industry best practices.`,
  purpose: `The purpose of this policy is to establish comprehensive standards and procedures for ${title.toLowerCase()} at Agency. This policy ensures compliance with the federal Conditions of Participation for Home Health Agencies as outlined in 42 CFR §484, which establishes requirements for quality patient care, organizational structure, clinical services, and administrative operations. Home health agencies must maintain policies and procedures that govern all aspects of service delivery to ensure patient safety, quality outcomes, and regulatory compliance.

This policy is designed to provide clear guidance to all Agency personnel regarding expectations, procedures, and responsibilities related to ${title.toLowerCase()}. By implementing standardized processes and protocols, the agency ensures consistency in service delivery, reduces variation in practice, minimizes risks, and promotes continuous quality improvement. The policy reflects current evidence-based practices, professional standards of care, and regulatory requirements applicable to home health agencies operating in ${state}.

Furthermore, this policy addresses requirements established by accrediting organizations including the Community Health Accreditation Partner (CHAP) and the Accreditation Commission for Health Care (ACHC), which set forth standards for organizational management, clinical excellence, and patient-centered care. The agency recognizes that comprehensive policies and procedures are essential to achieving and maintaining accreditation status and demonstrating commitment to quality and safety. This policy shall be reviewed annually and updated as needed to reflect changes in regulations, standards of practice, and organizational needs.`,
  procedure: [
    "Staff shall follow all established protocols and procedures",
    "Documentation shall be completed accurately and timely",
    "Any deviations from standard procedures must be reported to supervisor",
    "Regular training and competency verification shall be maintained",
    "Quality monitoring shall be conducted as per QAPI program"
  ],
  references: [
    "42 CFR 484 - Medicare Conditions of Participation",
    `${state} Home Health Agency Licensure Requirements`,
    "Agency Policy and Procedure Manual"
  ]
});

// Edit Policy Modal Component
const EditPolicyModal = ({ editingPolicy, onClose, onSave, darkMode, getPolicyData }) => {
  const content = editingPolicy ? getPolicyData(editingPolicy.id, editingPolicy.title) : {};
  const [formData, setFormData] = useState({
    id: editingPolicy?.id || '',
    title: editingPolicy?.title || '',
    hasStateInserts: editingPolicy?.hasStateInserts || false,
    policyStatement: content.policyStatement || '',
    purpose: content.purpose || '',
    procedure: Array.isArray(content.procedure) ? content.procedure.join('\n') : '',
    references: Array.isArray(content.references) ? content.references.join('\n') : ''
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {editingPolicy ? 'Edit Policy' : 'Add New Policy'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
            {!editingPolicy && (
              <div>
                <Label className={darkMode ? 'text-gray-200' : ''}>Policy ID *</Label>
                <Input
                  value={formData.id}
                  onChange={(e) => setFormData({...formData, id: e.target.value})}
                  placeholder="e.g., 2.9"
                  required
                  className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>
            )}
            
            <div>
              <Label className={darkMode ? 'text-gray-200' : ''}>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div>
              <Label className={darkMode ? 'text-gray-200' : ''}>Policy Statement</Label>
              <Textarea
                value={formData.policyStatement}
                onChange={(e) => setFormData({...formData, policyStatement: e.target.value})}
                rows={3}
                className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div>
              <Label className={darkMode ? 'text-gray-200' : ''}>Purpose</Label>
              <Textarea
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                rows={8}
                className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div>
              <Label className={darkMode ? 'text-gray-200' : ''}>Procedure (one item per line)</Label>
              <Textarea
                value={formData.procedure}
                onChange={(e) => setFormData({...formData, procedure: e.target.value})}
                rows={5}
                placeholder="Enter each procedure step on a new line"
                className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div>
              <Label className={darkMode ? 'text-gray-200' : ''}>References (one per line)</Label>
              <Textarea
                value={formData.references}
                onChange={(e) => setFormData({...formData, references: e.target.value})}
                rows={3}
                className={darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">
                <Save className="w-4 h-4 mr-2" />
                Save Policy
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


const PolicyProcedurePage = () => {
  const [selectedState, setSelectedState] = useState('California');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [chapters, setChapters] = useState(DEFAULT_CHAPTERS);
  const [policies, setPolicies] = useState({});
  const [policyContent, setPolicyContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(['Admin', 'Administrator', 'Super Admin'].includes(user.role));
  }, []);

  // Fetch chapters and policies from backend
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch chapters
      const chaptersRes = await api.get('/api/policies/chapters');
      if (chaptersRes.data?.chapters?.length > 0) {
        setChapters(chaptersRes.data.chapters);
      }
      
      // Fetch all policies
      const policiesRes = await api.get('/api/policies/');
      if (policiesRes.data?.policies) {
        // Group policies by chapter
        const grouped = {};
        const contentMap = {};
        policiesRes.data.policies.forEach(p => {
          if (!grouped[p.chapterId]) grouped[p.chapterId] = [];
          grouped[p.chapterId].push({
            id: p.policyId,
            title: p.title,
            hasStateInserts: p.hasStateInserts
          });
          contentMap[p.policyId] = p.content;
        });
        
        // Merge with defaults for chapters without policies
        Object.keys(DEFAULT_POLICIES).forEach(chId => {
          if (!grouped[chId]) {
            grouped[chId] = DEFAULT_POLICIES[chId];
          }
        });
        
        setPolicies(grouped);
        setPolicyContent(contentMap);
      } else {
        setPolicies(DEFAULT_POLICIES);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies(DEFAULT_POLICIES);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total policies
  const totalPolicies = useMemo(() => {
    return Object.values(policies).reduce((sum, arr) => sum + arr.length, 0);
  }, [policies]);

  // Get policy content
  const getPolicyData = (policyId, title) => {
    if (policyContent[policyId]) {
      return policyContent[policyId];
    }
    return generateDefaultContent(policyId, title, selectedState);
  };

  // Save policy
  const handleSavePolicy = async (policyData) => {
    try {
      if (editingPolicy) {
        // Update existing
        await api.put(`/api/policies/${editingPolicy.id}`, {
          title: policyData.title,
          hasStateInserts: policyData.hasStateInserts,
          content: {
            policyStatement: policyData.policyStatement,
            purpose: policyData.purpose,
            procedure: policyData.procedure?.split('\n').filter(Boolean) || [],
            references: policyData.references?.split('\n').filter(Boolean) || []
          }
        });
        toast.success('Policy updated successfully');
      } else {
        // Create new
        await api.post('/api/policies/', {
          policyId: policyData.id,
          title: policyData.title,
          chapterId: selectedChapter.chapterId,
          hasStateInserts: policyData.hasStateInserts || false,
          content: {
            policyStatement: policyData.policyStatement,
            purpose: policyData.purpose,
            procedure: policyData.procedure?.split('\n').filter(Boolean) || [],
            references: policyData.references?.split('\n').filter(Boolean) || []
          }
        });
        toast.success('Policy created successfully');
      }
      setEditMode(false);
      setEditingPolicy(null);
      setShowAddPolicy(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  // Delete policy
  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await api.delete(`/api/policies/${policyId}`);
      toast.success('Policy deleted');
      setSelectedPolicy(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleCloseModal = () => {
    setEditMode(false);
    setEditingPolicy(null);
    setShowAddPolicy(false);
  };

  // Render policy detail view
  const renderPolicyDetail = () => {
    const policy = selectedPolicy;
    const chapter = chapters.find(c => c.chapterId === selectedChapter?.chapterId);
    const content = getPolicyData(policy.id, policy.title);

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-gray-50 text-slate-900'}`}>
        {/* Header */}
        <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-teal-600'} py-4`}>
          <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-white" />
              <span className="text-white font-semibold text-lg">Home Health Policy Manager</span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 ${darkMode ? 'bg-slate-700' : 'bg-white/10'} px-3 py-1.5 rounded-lg`}>
                <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-teal-100'}`}>Current State (All 51 Jurisdictions):</span>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className={`px-3 py-1 rounded border-2 ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-teal-400 text-slate-800'} text-sm font-medium min-w-[150px]`}
                >
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedPolicy(null)} className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <Button variant="outline" size="sm" className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                <Settings className="w-4 h-4 mr-1" />Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDarkMode(!darkMode)} className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                {darkMode ? <Sun className="w-4 h-4 mr-1" /> : <Moon className="w-4 h-4 mr-1" />}
                {darkMode ? 'Light' : 'Dark'}
              </Button>
            </div>
          </div>
        </div>

        {/* Policy Content */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm p-8`}>
            {/* Policy Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'} mb-2`}>Policy {policy.id}</p>
                <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{policy.title}</h1>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditingPolicy(policy); setEditMode(true); }}>
                    <Edit className="w-4 h-4 mr-1" />Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeletePolicy(policy.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className={`mt-4 border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`} />

            {/* Policy Statement */}
            {content.policyStatement && (
              <div className="mb-8 mt-6">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'} mb-3`}>Policy Statement</h2>
                <p className={`${darkMode ? 'text-slate-300' : 'text-gray-700'} leading-relaxed`}>{content.policyStatement}</p>
              </div>
            )}

            {/* Purpose */}
            {content.purpose && (
              <div className="mb-8">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'} mb-3 uppercase`}>Purpose</h2>
                <div className={`${darkMode ? 'text-slate-300' : 'text-gray-700'} leading-relaxed whitespace-pre-line`}>
                  {content.purpose}
                </div>
              </div>
            )}

            {/* Procedure */}
            {content.procedure && content.procedure.length > 0 && (
              <div className="mb-8">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'} mb-3 uppercase`}>Policy</h2>
                <ol className={`list-decimal pl-6 space-y-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                  {content.procedure.map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* References */}
            {content.references && content.references.length > 0 && (
              <div className="mb-4">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'} mb-3`}>References</h2>
                <ul className={`list-disc pl-6 space-y-1 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                  {content.references.map((ref, idx) => (
                    <li key={idx}>{ref}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render chapter list
  const renderChapterList = () => {
    const chapter = selectedChapter;
    const chapterPolicies = policies[chapter.chapterId] || [];
    
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-gray-50 text-slate-900'}`}>
        {/* Header */}
        <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-teal-600'} py-4`}>
          <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-white" />
              <span className="text-white font-semibold text-lg">Home Health Policy Manager</span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 ${darkMode ? 'bg-slate-700' : 'bg-white/10'} px-3 py-1.5 rounded-lg`}>
                <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-teal-100'}`}>Current State (All 51 Jurisdictions):</span>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className={`px-3 py-1 rounded border-2 ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-teal-400 text-slate-800'} text-sm font-medium min-w-[150px]`}
                >
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedChapter(null)} className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <Button variant="outline" size="sm" className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                <Settings className="w-4 h-4 mr-1" />Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDarkMode(!darkMode)} className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
                {darkMode ? <Sun className="w-4 h-4 mr-1" /> : <Moon className="w-4 h-4 mr-1" />}
                {darkMode ? 'Light' : 'Dark'}
              </Button>
            </div>
          </div>
        </div>

        {/* Chapter Content */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`}>
            {/* Chapter Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{chapter.title}</h1>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'} mt-1`}>{chapterPolicies.length} policies</p>
              </div>
              {isAdmin && (
                <Button onClick={() => setShowAddPolicy(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />Add Policy
                </Button>
              )}
            </div>

            {/* Policy List */}
            <div className="divide-y divide-gray-100">
              {chapterPolicies.map((policy) => (
                <div
                  key={policy.id}
                  onClick={() => setSelectedPolicy(policy)}
                  className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
                    darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`text-sm font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'} min-w-[50px]`}>{policy.id}</span>
                    <span className={`${darkMode ? 'text-slate-200' : 'text-gray-800'}`}>{policy.title}</span>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render dashboard
  const renderDashboard = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-gray-50 text-slate-900'}`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-teal-600'} py-4`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-white" />
            <span className="text-white font-semibold text-lg">Home Health Policy Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 ${darkMode ? 'bg-slate-700' : 'bg-white/10'} px-3 py-1.5 rounded-lg`}>
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-teal-100'}`}>Current State (All 51 Jurisdictions):</span>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className={`px-3 py-1 rounded border-2 ${darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-teal-400 text-slate-800'} text-sm font-medium min-w-[150px]`}
              >
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <Button variant="outline" size="sm" className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
              <Settings className="w-4 h-4 mr-1" />Settings
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDarkMode(!darkMode)} className={`${darkMode ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}>
              {darkMode ? <Sun className="w-4 h-4 mr-1" /> : <Moon className="w-4 h-4 mr-1" />}
              {darkMode ? 'Light' : 'Dark'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-xl">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            <Input
              placeholder={`Search ${totalPolicies} policies...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-12 py-6 text-lg ${darkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-gray-200'}`}
            />
          </div>
        </div>

        {/* Chapters Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chapters.map((chapter) => {
              const IconComponent = ICONS[chapter.icon] || FileText;
              const chapterPolicies = policies[chapter.chapterId] || [];
              return (
                <div
                  key={chapter.chapterId}
                  onClick={() => setSelectedChapter(chapter)}
                  className={`${darkMode ? 'bg-slate-800 border-slate-700 hover:border-teal-500' : 'bg-white border-gray-200 hover:border-teal-400'} rounded-lg border p-6 cursor-pointer transition-all hover:shadow-lg`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: `${chapter.color}15` }}>
                      <IconComponent className="w-8 h-8" style={{ color: chapter.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-700'}`}>{chapter.title}</h3>
                      <span className="inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${chapter.color}15`, color: chapter.color }}>
                        {chapterPolicies.length} Policies
                      </span>
                      <p className={`text-sm mt-3 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>{chapter.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary */}
        <div className={`mt-8 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'} rounded-lg border p-4 text-center`}>
          <p className={`${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            <strong>{totalPolicies}</strong> comprehensive policies across{' '}
            <strong>{chapters.length}</strong> categories for{' '}
            <strong>{selectedState}</strong>
          </p>
        </div>
      </div>
    </div>
  );

  // Main render
  return (
    <DashboardLayout>
      <div data-testid="policy-procedure-page" className="-m-6">
        {selectedPolicy ? renderPolicyDetail() :
         selectedChapter ? renderChapterList() :
         renderDashboard()}
        
        {/* Edit Modal */}
        {(editMode || showAddPolicy) && (
          <EditPolicyModal 
            editingPolicy={editingPolicy}
            onClose={handleCloseModal}
            onSave={handleSavePolicy}
            darkMode={darkMode}
            getPolicyData={getPolicyData}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default PolicyProcedurePage;
